import { useState } from "react";
import { ArrowRightLeft, Copy, Percent, Wallet, Loader2, Download, FileSpreadsheet, FileText, Pencil, Save, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";

const fmt = (n) => Number(n || 0).toFixed(2);

export function KindBadge({ doc }) {
  if (doc.kind === "acompte") return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400" data-testid="kind-acompte">Acompte {doc.acompte_pct}%</span>;
  if (doc.kind === "solde") return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400" data-testid="kind-solde">Solde</span>;
  if (doc.type === "devis" && doc.converted_invoice_ref) return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/10 text-purple-300" data-testid="kind-converted">→ {doc.converted_invoice_ref}</span>;
  if (doc.type === "devis" && doc.acomptes?.length) return <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400">{doc.acomptes.length} acompte(s)</span>;
  return null;
}

export function LifecycleActions({ api, doc, onCreated }) {
  const [busy, setBusy] = useState("");
  const [showAcompte, setShowAcompte] = useState(false);
  const [pct, setPct] = useState(30);

  const run = async (key, url, body) => {
    setBusy(key);
    try {
      const r = await api.post(url, body || {});
      toast.success(`${r.data.reference} créé`);
      onCreated?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); }
    setBusy("");
  };

  const isDevis = doc.type === "devis";
  const canConvert = isDevis && !doc.converted_invoice_id && doc.status !== "annule" && !(doc.acomptes?.length);
  const canAcompte = isDevis && !doc.converted_invoice_id && doc.status !== "annule";
  const canSolde = isDevis && doc.acomptes?.length > 0 && !doc.solde_invoice_id;
  const acompteTtc = Math.round(Math.abs(doc.total_ttc || 0) * (parseFloat(pct) || 0)) / 100;

  return (
    <>
      {canConvert && (
        <Button onClick={() => run("convert", `/facturation/documents/${doc.invoice_id}/convert`)} disabled={!!busy} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] text-xs font-bold" data-testid="convert-devis-btn">
          {busy === "convert" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />} Convertir en facture
        </Button>
      )}
      {canAcompte && (
        <Button onClick={() => setShowAcompte(true)} variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 text-xs" data-testid="acompte-btn">
          <Percent className="w-4 h-4 mr-1" /> Facture d'acompte
        </Button>
      )}
      {canSolde && (
        <Button onClick={() => run("solde", `/facturation/documents/${doc.invoice_id}/solde`)} disabled={!!busy} variant="outline" className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs" data-testid="solde-btn">
          {busy === "solde" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Wallet className="w-4 h-4 mr-1" />} Facture de solde
        </Button>
      )}
      <Button onClick={() => run("dup", `/facturation/documents/${doc.invoice_id}/duplicate`)} disabled={!!busy} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="duplicate-btn">
        {busy === "dup" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Copy className="w-4 h-4 mr-1" />} Dupliquer
      </Button>

      <Dialog open={showAcompte} onOpenChange={setShowAcompte}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-sm" data-testid="acompte-dialog">
          <DialogHeader><DialogTitle>Facture d'acompte sur {doc.reference}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              {[20, 30, 40, 50].map(p => <button key={p} onClick={() => setPct(p)} className={`flex-1 text-xs py-1.5 rounded-lg border ${pct === p ? "border-[#f7c935] text-[#f7c935]" : "border-white/10 text-slate-400"}`} data-testid={`acompte-pct-${p}`}>{p} %</button>)}
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Pourcentage du devis TTC</label>
              <Input type="number" min={1} max={99} value={pct} onChange={e => setPct(e.target.value)} className="bg-[#161020] border-white/10 text-white" data-testid="acompte-pct-input" />
            </div>
            <div className="text-sm bg-[#161020] rounded-lg p-3 flex justify-between"><span className="text-slate-500">Montant TTC</span><span className="font-bold text-[#f7c935]" data-testid="acompte-amount">{fmt(acompteTtc)} €</span></div>
            <Button onClick={async () => { await run("acompte", `/facturation/documents/${doc.invoice_id}/acompte`, { percent: parseFloat(pct) }); setShowAcompte(false); }} disabled={!!busy || !(parseFloat(pct) > 0 && parseFloat(pct) < 100)} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="acompte-confirm-btn">
              {busy === "acompte" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Créer la facture d'acompte
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ExportMenu({ api, filter }) {
  const [busy, setBusy] = useState("");
  const dl = async (ext) => {
    setBusy(ext);
    try {
      const params = {};
      if (filter?.type) params.doc_type = filter.type;
      if (filter?.status) params.status = filter.status;
      if (filter?.client_id) params.client_id = filter.client_id;
      const r = await api.get(`/facturation/documents/export.${ext}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = `facturation.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export impossible"); }
    setBusy("");
  };
  return (
    <div className="flex gap-1">
      <Button onClick={() => dl("csv")} disabled={!!busy} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="export-csv-btn">
        {busy === "csv" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} CSV
      </Button>
      <Button onClick={() => dl("xlsx")} disabled={!!busy} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs" data-testid="export-xlsx-btn">
        {busy === "xlsx" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />} Excel
      </Button>
      <Download className="hidden" />
    </div>
  );
}

const CLIENT_FIELDS = [["client_name", "Nom / raison sociale"], ["client_email", "E-mail"], ["client_siret", "SIRET"], ["client_siren", "SIREN"], ["client_tva_intra", "TVA intra"], ["client_address", "Adresse"]];

export function ClientInfoEditor({ api, doc, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const editable = ["brouillon", "envoye"].includes(doc.status);

  const start = () => { setForm(Object.fromEntries(CLIENT_FIELDS.map(([k]) => [k, doc[k] || ""]))); setEdit(true); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/facturation/documents/${doc.invoice_id}`, form); toast.success("Infos client mises à jour"); setEdit(false); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  if (!edit) {
    return (
      <div className="grid grid-cols-2 gap-3 text-sm" data-testid="doc-client-info">
        <div><span className="text-slate-500">Client:</span> <span className="font-medium">{doc.client_name}</span></div>
        <div><span className="text-slate-500">Email:</span> <span>{doc.client_email || "-"}</span></div>
        {doc.client_address && <div className="col-span-2"><span className="text-slate-500">Adresse:</span> <span>{doc.client_address}</span></div>}
        {(doc.client_siret || doc.client_tva_intra) && <div className="col-span-2 text-xs text-slate-400">{doc.client_siret && `SIRET ${doc.client_siret}`} {doc.client_tva_intra && `· TVA ${doc.client_tva_intra}`}</div>}
        {editable && <button onClick={start} className="col-span-2 text-xs text-[#f7c935] hover:underline flex items-center gap-1 w-fit" data-testid="edit-client-info-btn"><Pencil className="w-3 h-3" /> Modifier les infos client sur ce document</button>}
      </div>
    );
  }
  return (
    <div className="bg-[#161020] rounded-lg p-3 space-y-2" data-testid="doc-client-editor">
      <div className="grid grid-cols-2 gap-2">
        {CLIENT_FIELDS.map(([k, l]) => (
          <div key={k} className={k === "client_address" || k === "client_name" ? "col-span-2" : ""}>
            <label className="text-[10px] uppercase text-slate-500 block mb-0.5">{l}</label>
            <Input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white h-8 text-xs" data-testid={`doc-${k}-input`} />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => setEdit(false)} className="border-white/10 text-slate-400 h-8 text-xs"><X className="w-3 h-3 mr-1" /> Annuler</Button>
        <Button size="sm" onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] h-8 text-xs font-bold" data-testid="save-client-info-btn">{saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Enregistrer</Button>
      </div>
    </div>
  );
}
